import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CoverImg } from "@/components/ui/CoverImg";
import { Skeleton } from "@/components/ui/Skeleton";
import { TrackList } from "@/components/tracks/TrackList";
import { musicService } from "@/services/musicService";
import { totalDurationMs } from "@/utils/selectors";
import { fmtDurationVerbose } from "@/utils/format";
import type { Album, Track } from "@/types/types";
import { useLibrary } from "@/hooks/useLibrary";
import { usePlayer } from "@/hooks/usePlayer";
import { useCoverColor } from "@/hooks/useCoverColor";
import { useStickyHeaderTrigger } from "@/hooks/useStickyHeaderTrigger";
import { usePreloadImage } from "@/hooks/usePreloadImage";
import { coverUrl } from "@/lib/cover";

export function AlbumPage() {
  const { id = "" } = useParams();
  const [album, setAlbum] = useState<Album | null>(null);
  const [tracks, setTracks] = useState<Track[]>([]);
  const { isLiked, toggle } = useLibrary();
  const p = usePlayer();
  const wash = useCoverColor(album?.cover_id);
  const { sentinelRef, visible: stickyVisible } = useStickyHeaderTrigger();
  usePreloadImage(album?.cover_id ? coverUrl(album.cover_id, 232) : null);

  useEffect(() => {
    musicService.album(id).then(setAlbum).catch(console.error);
    musicService.albumTracks(id).then(setTracks).catch(console.error);
  }, [id]);

  if (!album) return <Skeleton height={300} />;
  const liked = isLiked("album", album.id);

  return (
    <div className="page-hero-wrap">
      <div className="hero-wash" style={{
        background: wash
          ? `linear-gradient(180deg, ${wash} 0%, transparent 65%)`
          : "transparent",
      }} />

      <div className={"page-sticky-header" + (stickyVisible ? " visible" : "")}>
        <CoverImg coverId={album.cover_id} alt={album.title} size={36} />
        <strong className="trunc">{album.title}</strong>
        <div className="grow" />
        <button className="btn primary" style={{ padding: "8px 14px" }}
                onClick={() => void p.play(tracks, 0)}>▶</button>
      </div>

      <header className="hero" style={{
        display: "flex", flexDirection: "row", alignItems: "flex-end",
        gap: 28, marginBottom: 28,
      }}>
        <CoverImg coverId={album.cover_id} alt={album.title} size={232} priority />
        <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingBottom: 6, minWidth: 0 }}>
          <div className="muted" style={{ fontSize: 13 }}>Album</div>
          <h1 style={{ margin: 0, fontSize: 52, lineHeight: 1.05, fontWeight: 800 }}>
            {album.title}
          </h1>
          <div className="muted" style={{ fontSize: 14 }}>
            <Link to={`/artist/${album.artist.id}`} style={{ color: "inherit", fontWeight: 600 }}>
              {album.artist.name}
            </Link>
            {album.year ? ` · ${album.year}` : ""} · {tracks.length} titres · {fmtDurationVerbose(totalDurationMs(tracks))}
          </div>
          <div style={{ display: "flex", flexDirection: "row", gap: 8, marginTop: 10 }}>
            <button className="btn primary" onClick={() => void p.play(tracks, 0)}>▶ Lire</button>
            <button className="btn" onClick={() => void toggle("album", album.id)}>
              {liked ? "♥ Liké" : "Aimer l'album"}
            </button>
          </div>
        </div>
      </header>
      <div ref={sentinelRef} style={{ height: 1 }} aria-hidden="true" />

      <TrackList tracks={tracks} />
    </div>
  );
}
